/* eslint-disable react/no-unstable-nested-components */
import * as React from "react";
import { getCoreRowModel, Row, useReactTable } from "@tanstack/react-table";
import {
    Button,
    Checkbox,
    ColumnDef,
    Dialog,
    DialogContent,
    Dropdown,
    DropdownContent,
    DropdownItem,
    DropdownTrigger,
    FilterButton,
    MoreButton,
    Pagination,
    Table,
} from "@jhool-io/fe-components";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useIsMutating, useQueryClient } from "@tanstack/react-query";
import {
    IClaim,
    ClaimStatus,
    ClaimsHasRemit,
    ClaimActivityType,
    ClaimTableRow,
} from "../../../types/claims.types";
import {
    displayNameInRightFormat,
    formatDate,
    getTotalAmountOfTableRowsSelected,
    showMoneyInAppFormat,
} from "../../../../../utils/helpers";
import {
    getClaimActivityToDisplay,
    getClaimCptCodeEntries,
    getClaimCptCodesDisplay,
    getStatusTag,
    getClaimTagBadge,
    formatClaimStatus,
    getClaimStatusFilterLabel,
} from "../../../helpers/claims.helper";
import ListState from "../../../../../components/ListState/ListState";
import { useFetchClaims } from "../../../hooks/claims.queries";
import { IAppCustomDates } from "../../../../../utils/types";
import {
    useChangeClaimVoidStatus,
    useUpdateClaimsActivity,
    useUpdateClaimStatus,
} from "../../../hooks/claims.mutations";
import useToast from "../../../../../hooks/useToast";
import { LISTS_DEFAULT_LIMIT } from "../../../../../utils/constants";
import BulkUpdateClaimForm from "../BulkUpdateClaimsForm/BulkUpdateClaimsForm";
import FilterIcon from "../../../../../components/Icons/Filter";
import TableFiltersDisplay from "../../../../../shared-ui/TableFiltersDisplay/TableFiltersDisplay";
import { useFetchUserDetails } from "../../../../../hooks/queries/user";
import Loader from "../../../../../shared-ui/Loader/Loader";
import { Source } from "../../../../notes/types/notes.types";
import { useAllowedClaimStatuses } from "../../../hooks/helpers.hooks";

interface ClosedClaimsListProps {
    searchString: string;
}

type Dialogs = "bulk_update_status";

export default function ClosedClaimsList({
    searchString,
}: ClosedClaimsListProps) {
    const navigate = useNavigate();

    const [searchParams, setSearchParams] = useSearchParams();
    const [dialogInView, setDialogInView] = React.useState<Dialogs | null>(
        null
    );
    const [rowSelection, setRowSelection] = React.useState({});
    const [getSelectedClaimsData, setGetSelectedClaimsData] = React.useState<
        Row<IClaim>[]
    >([]);
    const [statusToSubmit, setStatusToSubmit] =
        React.useState<ClaimStatus | null>(null);

    const [columnVisibility, setColumnVisibility] = React.useState<
        Record<string, boolean>
    >({});

    const pageFilter = Number(searchParams.get("page") || 1);
    const limitFilter =
        Number(searchParams.get("limit")) || LISTS_DEFAULT_LIMIT;
    const fromDateFilter = searchParams.get("note_dos_from");
    const toDateFilter = searchParams.get("note_dos_to");
    const insuranceProviderIdsFilter =
        searchParams.getAll("insurance_providers[]") || [];
    const cptCodesFilter = searchParams.getAll("cpt_codes[]");
    const employerNamesFilter = searchParams.getAll("employer_names[]");
    const customDateFilter =
        (searchParams.get("date_range") as IAppCustomDates) ||
        IAppCustomDates.LAST_90_DAYS;
    const claimsStatusFilter = searchParams.get("status") as ClaimStatus;
    const hasRemitStatusFilter = searchParams.get("has_remits") || "";
    const rejectionReasonsFilter = searchParams.get("rejection_reasons") || "";
    const showArchivedNotesFilter =
        searchParams.get("show_archived_notes") || "";

    const [sortConfig, setSortConfig] = React.useState<{
        key: string;
        direction: string;
    }>({
        key: "",
        direction: "",
    });
    const showVoidedFilter = searchParams.get("show_voided") || "false";

    // Column filters
    const clientColFilter = searchParams.get("client_filter");
    const clinicianColFilter = searchParams.get("clinician_filter");
    const dosColFilter = searchParams.get("date_of_service_filter");
    const statusColFilter = searchParams.get("status_filter");
    const sourceColFilter = searchParams.get("source_filter");
    const insuraceColFilter = searchParams.get("insurance_filter");
    const cptColFilter = searchParams.get("cpt_filter");
    const acceptAssignFilter = searchParams.get("accepts_assignment_filter");
    const attachDocFilter = searchParams.get("attached_doc_filter");
    const claimsWithRemitsFilter = searchParams.get("has_remits_filter");
    const chargeAmountFilter = searchParams.get("charge_amount_filter") || "";
    const insuranceOutstandingAmountFilter =
        searchParams.get("insurance_outstanding_amount_filter") || "";
    const coInsuranceOutstandingAmountFilter =
        searchParams.get("coinsurance_outstanding_amount_filter") || "";

    const sortableColumns = [
        "generated_on",
        "date_of_service",
        "submitted_on",
        "insurance_name",
        "claim_status",
        "client_name",
        "cpt_code",
        "note_author",
    ];

    const loggedInUser = useFetchUserDetails();

    const patchClaimStatus = useUpdateClaimStatus();

    const queryClient = useQueryClient();

    const isMutating = useIsMutating(["bulk-update-claims"]);

    // hook for voiding/unvoiding claim
    const changeVoidStatus = useChangeClaimVoidStatus();
    const updateClaimsActivity = useUpdateClaimsActivity();

    const { toast } = useToast();

    const getStatusFilter = () => {
        if (claimsStatusFilter) {
            if ([ClaimStatus.CLOSED].includes(claimsStatusFilter)) {
                return [claimsStatusFilter];
            }

            return [""];
        }

        return [ClaimStatus.CLOSED];
    };
    const handleConfigAttribute = () => {
        if (sortableColumns.includes(sortConfig.key)) {
            return sortConfig.key;
        }

        return undefined;
    };

    const getHasRemitFilter = () => {
        if (hasRemitStatusFilter === ClaimsHasRemit.SHOW_CLAIMS_WITH_REMIT) {
            return "true";
        }
        if (hasRemitStatusFilter === ClaimsHasRemit.HIDE_CLAIMS_WITH_REMIT) {
            return "false";
        }
        return "";
    };

    const claimSourceFilter = searchParams.get("source") || "";

    const { data, error, isLoading, isSuccess } = useFetchClaims({
        claim_statuses: getStatusFilter(),
        custom_date: customDateFilter,
        search_string: searchString,
        note_dos_from: fromDateFilter || undefined,
        note_dos_to: toDateFilter || undefined,
        insurance_provider_ids: insuranceProviderIdsFilter,
        cpt_codes: cptCodesFilter,
        employer_names: employerNamesFilter,
        page: pageFilter,
        limit: limitFilter,
        has_remits: getHasRemitFilter(),
        sort_attribute:
            searchParams.get("closed_claims_sort_attr") ||
            handleConfigAttribute(),
        sort_order:
            searchParams.get("closed_claims_sort_order") ||
            sortConfig.direction,
        claim_source: claimSourceFilter,
        show_voided: showVoidedFilter,
        status_note_categories: rejectionReasonsFilter
            ? [rejectionReasonsFilter]
            : undefined,
        show_old_claims: Boolean(searchString),
        show_archived_notes: showArchivedNotesFilter,
    });

    const selectedClaimsIds =
        getSelectedClaimsData &&
        getSelectedClaimsData.map(
            (item: Row<IClaim>) => item.original.claim_id
        );

    const selectedClaimsStatuses = getSelectedClaimsData.map(
        (row) => row.original.claim_status
    );

    const allowedClaimStatuses = useAllowedClaimStatuses(
        selectedClaimsStatuses
    );

    const claimsDetails =
        selectedClaimsIds &&
        selectedClaimsIds.map((id: string) =>
            data?.data.find((claim) => claim.claim_id === id)
        );

    const showUnvoidClaimsButton =
        claimsDetails &&
        claimsDetails.length > 0 &&
        claimsDetails.length ===
            claimsDetails.filter((claim) => claim && claim.is_voided).length;

    const showVoidClaimsButton =
        claimsDetails &&
        claimsDetails.length > 0 &&
        claimsDetails.length ===
            claimsDetails.filter((claim) => claim && !claim.is_voided).length;

    const showUnassignClaimsButton =
        claimsDetails &&
        claimsDetails.length > 0 &&
        claimsDetails.length ===
            claimsDetails.filter((claim) => {
                const { isClaimAssignedToMe } = getClaimActivityToDisplay(
                    claim?.claim_activities || [],
                    ClaimActivityType.IN_PROGRESS,
                    loggedInUser?.data?.user_id ?? ""
                );

                return isClaimAssignedToMe;
            }).length;

    const showAssignClaimsButton =
        claimsDetails &&
        claimsDetails.length > 0 &&
        claimsDetails.length ===
            claimsDetails.filter((claim) => {
                const { isClaimAssignedToMe } = getClaimActivityToDisplay(
                    claim?.claim_activities || [],
                    ClaimActivityType.IN_PROGRESS,
                    loggedInUser?.data?.user_id ?? ""
                );

                return !isClaimAssignedToMe;
            }).length;

    const handleChangeStatus = (id: string, status: ClaimStatus) => {
        const changeStatusPayload = {
            claim_id: id,
            claim_status: status,
        };
        patchClaimStatus.mutate(changeStatusPayload, {
            onSuccess: async (response) => {
                await queryClient.invalidateQueries(["claims"]);
                toast({
                    mode: "success",
                    message:
                        response?.message ||
                        "Claim status updated successfully",
                });
            },
            onError: (err) => {
                toast({
                    mode: "error",
                    message:
                        err.response?.data.message ||
                        "Could not update claim status at this time",
                });
            },
        });
    };

    const handleChangeVoidStatus = (status: boolean, id?: string) => {
        const payload = {
            claim_ids: id ? [id] : selectedClaimsIds,
            void_status: status,
        };

        changeVoidStatus.mutate(payload, {
            onSuccess: (response) => {
                queryClient.invalidateQueries(["claims"]);
                toast({
                    mode: "success",
                    message:
                        response?.message ||
                        `Claim ${
                            status === true ? "voided" : "unvoided"
                        } successfully`,
                });
                setRowSelection({});
            },
            onError: (err) => {
                toast({
                    mode: "error",
                    message:
                        err.response?.data.message ||
                        `Could not ${
                            status === true ? "void" : "unvoid"
                        } claim at this time`,
                });
            },
        });
    };

    const handleDialogInView = (dialog: Dialogs | null) => {
        setDialogInView(dialog);
    };

    const handleDisplayBulkUpdateStatusDialog = (status: ClaimStatus) => {
        setStatusToSubmit(status);
        handleDialogInView("bulk_update_status");
    };

    const handleCloseBulkUpdateStatusDialog = () => {
        setStatusToSubmit(null);
        setRowSelection({});
        handleDialogInView(null);
    };

    const handleRowClick = (row: Row<IClaim>) => {
        if (Array.isArray(row.original.claim_contents)) {
            navigate(
                `/claims/group/${row.original.claim_id}/${row.original.client_id}` +
                    `?key=claims&claims_view=view&viewFrom=claims&tab=closed` +
                    `&claims_sub_tab=claims&active_claim_id=${row.original.claim_id}`
            );
            return;
        }

        navigate(
            `/claims/${row.original.claim_id}/${row.original.client_id}/${row.original.note_id}?key=claims&claims_view=view&viewFrom=claims&tab=closed&claims_sub_tab=claims&active_claim_id=${row.original.claim_id}`
        );
    };

    // onChange handler for page
    const handlePageChange = (page: number) => {
        searchParams.set("page", String(page));
        setSearchParams(searchParams);
    };

    const handleLimitChange = (limit: number) => {
        searchParams.set("page", String(limit));
        setSearchParams(searchParams);
    };

    const handleUpdateClaimsActivity = (
        activityType: ClaimActivityType,
        claimId?: string
    ) => {
        const dataToSend = {
            claim_ids: claimId ? [claimId] : selectedClaimsIds,
            activity_type: activityType,
            notes: undefined,
        };
        updateClaimsActivity.mutate(dataToSend, {
            onSuccess: async (res) => {
                await queryClient.invalidateQueries(["claims"]);

                toast({
                    mode: "success",
                    message:
                        res.message || "Claims activity updated successfully!",
                });
                setRowSelection({});
            },
            onError: (err) => {
                toast({
                    mode: "error",
                    message:
                        err.response?.data.message ||
                        "Could not perform action at this time.",
                });
            },
        });
    };

    const datesOfServiceInClaims = React.useMemo(() => {
        return [
            ...new Set(
                data?.data
                    ?.map((claim: IClaim) => claim.date_of_service)
                    .filter(Boolean)
            ),
        ].sort((a, b) => new Date(b).getTime() - new Date(a).getTime());
    }, [data?.data]);

    const clientsInClaims = React.useMemo(() => {
        return [
            ...new Set(
                data?.data
                    ?.map((claim: IClaim) => claim.client_name)
                    .filter(Boolean)
            ),
        ];
    }, [data?.data]);

    const noteAuthorsInClaims = React.useMemo(() => {
        return [
            ...new Set(
                data?.data
                    ?.filter((claim: IClaim) => claim.note_author)
                    .map(
                        (claim: IClaim) =>
                            `${claim.note_author?.first_name} ${claim.note_author?.last_name}`
                    )
            ),
        ];
    }, [data?.data]);

    const statusesInClaims = React.useMemo(() => {
        return [
            ...new Set(
                data?.data
                    ?.map((claim: IClaim) => claim.claim_status)
                    .filter(Boolean)
            ),
        ];
    }, [data?.data]);

    const sourcesInClaims = React.useMemo(() => {
        return [
            ...new Set(
                data?.data
                    ?.map((claim: IClaim) => claim.claim_source)
                    .filter(Boolean)
            ),
        ];
    }, [data?.data]);

    const insuranceProvidersInClaims = React.useMemo(() => {
        return (
            data?.data?.reduce(
                (acc: { id: string; name: string }[], claim: IClaim) => {
                    if (
                        !acc.some(
                            (provider) =>
                                provider.id === claim.insurance_provider_id
                        )
                    ) {
                        acc.push({
                            id: claim.insurance_provider_id,
                            name: claim.insurance_name,
                        });
                    }
                    return acc;
                },
                []
            ) || []
        );
    }, [data?.data]);

    const cptCodesInClaims = React.useMemo(() => {
        return [
            ...new Set(
                data?.data?.flatMap((claim: IClaim) =>
                    getClaimCptCodeEntries(claim)
                )
            ),
        ].sort();
    }, [data?.data]);

    const assignmentInClaims = React.useMemo(() => {
        return [
            ...new Set(
                data?.data?.map((claim: IClaim) =>
                    claim.accepts_assignment ? "Yes" : "No"
                )
            ),
        ];
    }, [data?.data]);

    const claimsWithRemits = React.useMemo(() => {
        return [
            ...new Set(
                data?.data?.map((claim: IClaim) =>
                    claim.has_remits ? "Yes" : "No"
                )
            ),
        ];
    }, [data?.data]);

    const attachDocInClaims = React.useMemo(() => {
        return [
            ...new Set(
                data?.data?.map((claim: IClaim) =>
                    claim.has_claim_attachments ? "Yes" : "No"
                )
            ),
        ];
    }, [data?.data]);

    const chargeAmountInClaims = React.useMemo(() => {
        return [
            ...new Set(data?.data?.map((claim: IClaim) => claim.charge_amount)),
        ].sort();
    }, [data?.data]);

    const insuranceAmountOutstandingInClaims = React.useMemo(() => {
        return [
            ...new Set(
                data?.data?.map(
                    (claim: IClaim) => claim.insurance_amount_outstanding
                )
            ),
        ].sort();
    }, [data?.data]);

    const coInsuranceAmountOutstandingInClaims = React.useMemo(() => {
        return [
            ...new Set(
                data?.data?.map(
                    (claim: IClaim) => claim.coinsurance_amount_outstanding
                )
            ),
        ].sort();
    }, [data?.data]);

    const appliedFilters = React.useMemo(() => {
        const filters = new Set<{ key: string; values: string[] }>();

        if (clientColFilter) {
            filters.add({
                key: "client_filter",
                values: [clientColFilter],
            });
        }
        if (clinicianColFilter) {
            filters.add({
                key: "clinician_filter",
                values: [clinicianColFilter],
            });
        }
        if (dosColFilter) {
            filters.add({
                key: "date_of_service_filter",
                values: [dosColFilter],
            });
        }
        if (insuraceColFilter) {
            filters.add({
                key: "insurance_filter",
                values: [insuraceColFilter],
            });
        }
        if (statusColFilter) {
            filters.add({
                key: "status_filter",
                values: [statusColFilter],
            });
        }
        if (sourceColFilter) {
            filters.add({
                key: "source_filter",
                values: [sourceColFilter],
            });
        }
        if (cptColFilter) {
            filters.add({
                key: "cpt_filter",
                values: [cptColFilter],
            });
        }
        if (claimsWithRemitsFilter) {
            filters.add({
                key: "has_remits_filter",
                values: [claimsWithRemitsFilter === "true" ? "Yes" : "No"],
            });
        }
        if (acceptAssignFilter) {
            filters.add({
                key: "accepts_assignment_filter",
                values: [acceptAssignFilter],
            });
        }
        if (attachDocFilter) {
            filters.add({
                key: "attached_doc_filter",
                values: [attachDocFilter === "true" ? "Yes" : "No"],
            });
        }

        if (insuranceOutstandingAmountFilter) {
            filters.add({
                key: "insurance_outstanding_amount_filter",
                values: [
                    showMoneyInAppFormat(insuranceOutstandingAmountFilter),
                ],
            });
        }
        if (coInsuranceOutstandingAmountFilter) {
            filters.add({
                key: "coinsurance_outstanding_amount_filter",
                values: [
                    showMoneyInAppFormat(coInsuranceOutstandingAmountFilter),
                ],
            });
        }
        if (chargeAmountFilter) {
            filters.add({
                key: "charge_amount_filter",
                values: [showMoneyInAppFormat(chargeAmountFilter)],
            });
        }

        return Array.from(filters);
    }, [
        clientColFilter,
        clinicianColFilter,
        dosColFilter,
        insuraceColFilter,
        statusColFilter,
        sourceColFilter,
        cptColFilter,
        claimsWithRemitsFilter,
        acceptAssignFilter,
        attachDocFilter,
        insuranceOutstandingAmountFilter,
        coInsuranceOutstandingAmountFilter,
        chargeAmountFilter,
    ]);

    const filteredData = React.useMemo(() => {
        if (!data?.data || !isSuccess) return [];

        let filtered = data.data;

        if (clientColFilter) {
            filtered = filtered.filter(
                (claim: IClaim) => claim.client_name === clientColFilter
            );
        }

        if (clinicianColFilter) {
            filtered = filtered.filter((claim: IClaim) => {
                if (!claim.note_author) return false;
                const fullName = `${claim.note_author.first_name} ${claim.note_author.last_name}`;
                return fullName === clinicianColFilter;
            });
        }

        if (dosColFilter) {
            filtered = filtered.filter(
                (claim: IClaim) =>
                    formatDate(claim.date_of_service) === dosColFilter
            );
        }

        if (insuraceColFilter) {
            filtered = filtered.filter(
                (claim: IClaim) => claim.insurance_name === insuraceColFilter
            );
        }

        if (cptColFilter) {
            filtered = filtered.filter((claim: IClaim) =>
                getClaimCptCodeEntries(claim).includes(cptColFilter)
            );
        }

        if (statusColFilter) {
            filtered = filtered.filter(
                (claim: IClaim) => claim.claim_status === statusColFilter
            );
        }

        if (sourceColFilter) {
            filtered = filtered.filter(
                (claim: IClaim) => claim.claim_source === sourceColFilter
            );
        }

        if (acceptAssignFilter === "true") {
            filtered = filtered.filter(
                (claim: IClaim) => claim.accepts_assignment
            );
        }
        if (acceptAssignFilter === "false") {
            filtered = filtered.filter(
                (claim: IClaim) => !claim.accepts_assignment
            );
        }

        if (attachDocFilter === "true") {
            filtered = filtered.filter(
                (claim: IClaim) => claim.has_claim_attachments
            );
        }
        if (attachDocFilter === "false") {
            filtered = filtered.filter(
                (claim: IClaim) => !claim.has_claim_attachments
            );
        }

        if (claimsWithRemitsFilter === "true") {
            filtered = filtered.filter((claim: IClaim) => claim.has_remits);
        }
        if (claimsWithRemitsFilter === "false") {
            filtered = filtered.filter((claim: IClaim) => !claim.has_remits);
        }

        if (coInsuranceOutstandingAmountFilter) {
            filtered = filtered.filter(
                (claim: IClaim) =>
                    typeof claim.coinsurance_amount_outstanding === "number" &&
                    String(claim.coinsurance_amount_outstanding) ===
                        coInsuranceOutstandingAmountFilter
            );
        }

        if (chargeAmountFilter) {
            filtered = filtered.filter(
                (claim: IClaim) =>
                    typeof claim.charge_amount === "number" &&
                    String(claim.charge_amount) === chargeAmountFilter
            );
        }

        if (insuranceOutstandingAmountFilter) {
            filtered = filtered.filter(
                (claim: IClaim) =>
                    typeof claim.insurance_amount_outstanding === "number" &&
                    String(claim.insurance_amount_outstanding) ===
                        insuranceOutstandingAmountFilter
            );
        }

        return filtered;
    }, [
        acceptAssignFilter,
        attachDocFilter,
        claimsWithRemitsFilter,
        clientColFilter,
        clinicianColFilter,
        cptColFilter,
        data?.data,
        dosColFilter,
        insuraceColFilter,
        isSuccess,
        sourceColFilter,
        statusColFilter,
        coInsuranceOutstandingAmountFilter,
        chargeAmountFilter,
        insuranceOutstandingAmountFilter,
    ]);

    const handleColumnFilter = (paramName: string, value?: string | number) => {
        if (
            typeof value === "undefined" ||
            (typeof value === "string" && value === "")
        ) {
            searchParams.delete(paramName);
        } else if (typeof value === "number") {
            searchParams.set(paramName, String(value));
        } else {
            searchParams.set(
                paramName,
                paramName === "date_of_service_filter"
                    ? formatDate(value)
                    : value
            );
        }

        setSearchParams(searchParams);
    };

    const columns: ColumnDef<IClaim>[] = [
        {
            id: "select",
            header: ({ table }) => (
                <Checkbox
                    handleChange={table.getToggleAllRowsSelectedHandler()}
                    indeterminate={table.getIsSomeRowsSelected()}
                    label=""
                    name="row"
                    value="row"
                    isChecked={table.getIsAllRowsSelected()}
                />
            ),
            cell: ({ row }) => (
                <Checkbox
                    handleChange={row.getToggleSelectedHandler()}
                    indeterminate={row.getIsSomeSelected()}
                    label=""
                    name="row"
                    value="row"
                    isChecked={row.getIsSelected()}
                />
            ),
        },
        {
            header: " ",
            columns: [
                {
                    accessorKey: "client_name",
                    header: "CLIENT NAME",
                    cell: ({ row }: ClaimTableRow) => (
                        <div className="flex flex-col gap-y-1">
                            <span>
                                {displayNameInRightFormat(
                                    row.original.client_name
                                )}
                            </span>
                            {getClaimTagBadge(row.original.claim_tag)}
                        </div>
                    ),
                    filterComponent: (
                        <Dropdown>
                            <DropdownTrigger asChild>
                                <Button
                                    size="auto"
                                    variant="can-hover"
                                    onClick={(e) => e.stopPropagation()}
                                    className="px-1 h-6"
                                >
                                    <FilterIcon />
                                </Button>
                            </DropdownTrigger>
                            <DropdownContent width={270}>
                                <>
                                    <DropdownItem
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleColumnFilter("client_filter");
                                        }}
                                        isSelected={!clientColFilter}
                                        closeMenuOnSelect
                                    >
                                        All
                                    </DropdownItem>
                                    {clientsInClaims &&
                                        clientsInClaims.map((client) => (
                                            <DropdownItem
                                                key={client}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleColumnFilter(
                                                        "client_filter",
                                                        client
                                                    );
                                                }}
                                                isSelected={
                                                    clientColFilter === client
                                                }
                                            >
                                                {displayNameInRightFormat(
                                                    client
                                                )}
                                            </DropdownItem>
                                        ))}
                                </>
                            </DropdownContent>
                        </Dropdown>
                    ),
                },
            ],
        },
        {
            header: " ",
            columns: [
                {
                    accessorKey: "date_of_service",
                    header: "DATE OF SERVICE",
                    cell: ({ row }: ClaimTableRow) => (
                        <span>{formatDate(row.original.date_of_service)}</span>
                    ),
                    filterComponent: (
                        <Dropdown>
                            <DropdownTrigger asChild>
                                <Button
                                    size="auto"
                                    variant="can-hover"
                                    onClick={(e) => e.stopPropagation()}
                                    className="px-1 h-6"
                                >
                                    <FilterIcon />
                                </Button>
                            </DropdownTrigger>
                            <DropdownContent width={270}>
                                <DropdownItem
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleColumnFilter(
                                            "date_of_service_filter"
                                        );
                                    }}
                                    isSelected={!dosColFilter}
                                    closeMenuOnSelect
                                >
                                    All
                                </DropdownItem>
                                {datesOfServiceInClaims.map((option) => (
                                    <DropdownItem
                                        key={option}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleColumnFilter(
                                                "date_of_service_filter",
                                                option
                                            );
                                        }}
                                        isSelected={dosColFilter === option}
                                    >
                                        {formatDate(option)}
                                    </DropdownItem>
                                ))}
                            </DropdownContent>
                        </Dropdown>
                    ),
                },
            ],
        },
        {
            header: " ",
            columns: [
                {
                    accessorKey: "claim_status",
                    header: "STATUS",
                    cell: ({ row }: ClaimTableRow) => (
                        <span>{getStatusTag(row.original.claim_status)}</span>
                    ),
                    filterComponent: (
                        <Dropdown>
                            <DropdownTrigger asChild>
                                <Button
                                    size="auto"
                                    variant="can-hover"
                                    onClick={(e) => e.stopPropagation()}
                                    className="px-1 h-6"
                                >
                                    <FilterIcon />
                                </Button>
                            </DropdownTrigger>
                            <DropdownContent width={270}>
                                <>
                                    <DropdownItem
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleColumnFilter("status_filter");
                                        }}
                                        isSelected={!statusColFilter}
                                        closeMenuOnSelect
                                    >
                                        All
                                    </DropdownItem>
                                    {statusesInClaims &&
                                        statusesInClaims.map((status) => (
                                            <DropdownItem
                                                key={status}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleColumnFilter(
                                                        "status_filter",
                                                        status
                                                    );
                                                }}
                                                isSelected={
                                                    statusColFilter === status
                                                }
                                            >
                                                {getClaimStatusFilterLabel(
                                                    status
                                                )}
                                            </DropdownItem>
                                        ))}
                                </>
                            </DropdownContent>
                        </Dropdown>
                    ),
                },
            ],
        },
        {
            header: " ",
            columns: [
                {
                    accessorKey: "claim_activities",
                    header: "Assigned to",
                    cell: ({ row }: ClaimTableRow) => {
                        const { usersNameToDisplay } =
                            getClaimActivityToDisplay(
                                row.original.claim_activities,
                                ClaimActivityType.IN_PROGRESS,
                                loggedInUser?.data?.user_id ?? ""
                            );

                        return usersNameToDisplay;
                    },
                },
            ],
        },
        {
            header: " ",
            columns: [
                {
                    accessorKey: "claim_source",
                    header: "CLAIM SOURCE",
                    cell: ({ row }: ClaimTableRow) => (
                        <span>{row.original.claim_source || "--"}</span>
                    ),
                    filterComponent: (
                        <Dropdown>
                            <DropdownTrigger asChild>
                                <Button
                                    size="auto"
                                    variant="can-hover"
                                    onClick={(e) => e.stopPropagation()}
                                    className="px-1 h-6"
                                >
                                    <FilterIcon />
                                </Button>
                            </DropdownTrigger>
                            <DropdownContent width={270}>
                                <>
                                    <DropdownItem
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleColumnFilter("source_filter");
                                        }}
                                        isSelected={!sourceColFilter}
                                        closeMenuOnSelect
                                    >
                                        All
                                    </DropdownItem>
                                    {sourcesInClaims.length > 0 &&
                                        sourcesInClaims.map((source) => (
                                            <DropdownItem
                                                key={source}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleColumnFilter(
                                                        "source_filter",
                                                        source as Source
                                                    );
                                                }}
                                                isSelected={
                                                    sourceColFilter === source
                                                }
                                            >
                                                {source}
                                            </DropdownItem>
                                        ))}
                                </>
                            </DropdownContent>
                        </Dropdown>
                    ),
                },
            ],
        },
        {
            header: " ",
            columns: [
                {
                    accessorKey: "insurance_name",
                    header: "INSURANCE",
                    cell: ({ row }: ClaimTableRow) => (
                        <span className="uppercase">
                            {row.original.insurance_name}
                        </span>
                    ),
                    filterComponent: (
                        <Dropdown>
                            <DropdownTrigger asChild>
                                <Button
                                    size="auto"
                                    variant="can-hover"
                                    onClick={(e) => e.stopPropagation()}
                                    className="px-1 h-6"
                                >
                                    <FilterIcon />
                                </Button>
                            </DropdownTrigger>
                            <DropdownContent width={270}>
                                <>
                                    <DropdownItem
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleColumnFilter(
                                                "insurance_filter"
                                            );
                                        }}
                                        isSelected={!insuraceColFilter}
                                        closeMenuOnSelect
                                    >
                                        All
                                    </DropdownItem>
                                    {insuranceProvidersInClaims &&
                                        insuranceProvidersInClaims.map(
                                            (insurance) => (
                                                <DropdownItem
                                                    key={insurance.id}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleColumnFilter(
                                                            "insurance_filter",
                                                            insurance.name
                                                        );
                                                    }}
                                                    isSelected={
                                                        insurance.name ===
                                                        insuraceColFilter
                                                    }
                                                >
                                                    {insurance.name}
                                                </DropdownItem>
                                            )
                                        )}
                                </>
                            </DropdownContent>
                        </Dropdown>
                    ),
                },
            ],
        },
        {
            header: " ",
            columns: [
                {
                    accessorKey: "note_author",
                    header: "CLINICIAN",
                    cell: ({ row }: ClaimTableRow) => (
                        <span>
                            {row.original.note_author
                                ? displayNameInRightFormat({
                                      firstName:
                                          row.original.note_author.first_name,
                                      lastName:
                                          row.original.note_author.last_name,
                                  })
                                : "--"}
                        </span>
                    ),
                    filterComponent: (
                        <Dropdown>
                            <DropdownTrigger asChild>
                                <Button
                                    size="auto"
                                    variant="can-hover"
                                    onClick={(e) => e.stopPropagation()}
                                    className="px-1 h-6"
                                >
                                    <FilterIcon />
                                </Button>
                            </DropdownTrigger>
                            <DropdownContent width={270}>
                                <>
                                    <DropdownItem
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleColumnFilter(
                                                "clinician_filter"
                                            );
                                        }}
                                        isSelected={!clinicianColFilter}
                                        closeMenuOnSelect
                                    >
                                        All
                                    </DropdownItem>
                                    {noteAuthorsInClaims &&
                                        noteAuthorsInClaims.map((author) => (
                                            <DropdownItem
                                                key={author}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleColumnFilter(
                                                        "clinician_filter",
                                                        author
                                                    );
                                                }}
                                                isSelected={
                                                    clinicianColFilter ===
                                                    author
                                                }
                                            >
                                                {displayNameInRightFormat(
                                                    author
                                                )}
                                            </DropdownItem>
                                        ))}
                                </>
                            </DropdownContent>
                        </Dropdown>
                    ),
                },
            ],
        },
        {
            header: " ",
            columns: [
                {
                    accessorKey: "cpt_code",
                    header: "CPT CODE",
                    cell: ({ row }: ClaimTableRow) => (
                        <span>{getClaimCptCodesDisplay(row.original)}</span>
                    ),
                    filterComponent: (
                        <Dropdown>
                            <DropdownTrigger asChild>
                                <Button
                                    size="auto"
                                    variant="can-hover"
                                    onClick={(e) => e.stopPropagation()}
                                    className="px-1 h-6"
                                >
                                    <FilterIcon />
                                </Button>
                            </DropdownTrigger>
                            <DropdownContent
                                width={270}
                                maxHeight={216}
                                className="overflow-y-auto"
                            >
                                <>
                                    <DropdownItem
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleColumnFilter("cpt_filter");
                                        }}
                                        isSelected={!cptColFilter}
                                        closeMenuOnSelect
                                    >
                                        All
                                    </DropdownItem>
                                    {cptCodesInClaims &&
                                        cptCodesInClaims.map((code) => (
                                            <DropdownItem
                                                key={code}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleColumnFilter(
                                                        "cpt_filter",
                                                        code
                                                    );
                                                }}
                                                isSelected={
                                                    cptColFilter === code
                                                }
                                            >
                                                {code}
                                            </DropdownItem>
                                        ))}
                                </>
                            </DropdownContent>
                        </Dropdown>
                    ),
                },
            ],
        },
        {
            header: " ",
            columns: [
                {
                    accessorKey: "charge_amount",
                    header: "CHARGE",
                    cell: ({ row }: ClaimTableRow) => (
                        <span>
                            {showMoneyInAppFormat(row.original.charge_amount)}
                        </span>
                    ),
                    filterComponent: (
                        <Dropdown>
                            <DropdownTrigger asChild>
                                <Button
                                    variant="can-hover"
                                    size="auto"
                                    onClick={(e) => e.stopPropagation()}
                                    className="px-1 h-6"
                                >
                                    <FilterIcon />
                                </Button>
                            </DropdownTrigger>
                            <DropdownContent
                                width={270}
                                className="overflow-y-auto"
                            >
                                <>
                                    <DropdownItem
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleColumnFilter(
                                                "charge_amount_filter"
                                            );
                                        }}
                                        isSelected={!chargeAmountFilter}
                                        closeMenuOnSelect
                                    >
                                        All
                                    </DropdownItem>
                                    {chargeAmountInClaims &&
                                        chargeAmountInClaims.map((amount) => (
                                            <DropdownItem
                                                key={amount}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleColumnFilter(
                                                        "charge_amount_filter",
                                                        amount
                                                    );
                                                }}
                                                isSelected={
                                                    amount && amount >= 0
                                                        ? chargeAmountFilter ===
                                                          String(amount)
                                                        : false
                                                }
                                            >
                                                {showMoneyInAppFormat(amount)}
                                            </DropdownItem>
                                        ))}
                                </>
                            </DropdownContent>
                        </Dropdown>
                    ),
                },
            ],
        },
        {
            header: "OUTSTANDING",
            columns: [
                {
                    accessorKey: "insurance_amount_outstanding",
                    header: "INSURANCE",
                    cell: ({ row }: ClaimTableRow) => (
                        <span>
                            {showMoneyInAppFormat(
                                row.original.insurance_amount_outstanding
                            )}
                        </span>
                    ),
                    filterComponent: (
                        <Dropdown>
                            <DropdownTrigger asChild>
                                <Button
                                    variant="can-hover"
                                    size="auto"
                                    onClick={(e) => e.stopPropagation()}
                                    className="px-1 h-6"
                                >
                                    <FilterIcon />
                                </Button>
                            </DropdownTrigger>
                            <DropdownContent
                                width={270}
                                className="overflow-y-auto"
                            >
                                <>
                                    <DropdownItem
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleColumnFilter(
                                                "insurance_outstanding_amount_filter"
                                            );
                                        }}
                                        isSelected={
                                            !insuranceOutstandingAmountFilter
                                        }
                                        closeMenuOnSelect
                                    >
                                        All
                                    </DropdownItem>
                                    {insuranceAmountOutstandingInClaims &&
                                        insuranceAmountOutstandingInClaims.map(
                                            (amount) => (
                                                <DropdownItem
                                                    key={amount}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleColumnFilter(
                                                            "insurance_outstanding_amount_filter",
                                                            amount
                                                        );
                                                    }}
                                                    isSelected={
                                                        amount && amount >= 0
                                                            ? insuranceOutstandingAmountFilter ===
                                                              String(amount)
                                                            : false
                                                    }
                                                >
                                                    {showMoneyInAppFormat(
                                                        amount
                                                    )}
                                                </DropdownItem>
                                            )
                                        )}
                                </>
                            </DropdownContent>
                        </Dropdown>
                    ),
                },
                {
                    accessorKey: "coinsurance_amount_outstanding",
                    header: "CO-INSURANCE",
                    cell: ({ row }: ClaimTableRow) => (
                        <span>
                            {showMoneyInAppFormat(
                                row.original.coinsurance_amount_outstanding
                            )}
                        </span>
                    ),
                    filterComponent: (
                        <Dropdown>
                            <DropdownTrigger asChild>
                                <Button
                                    variant="can-hover"
                                    size="auto"
                                    onClick={(e) => e.stopPropagation()}
                                    className="px-1 h-6"
                                >
                                    <FilterIcon />
                                </Button>
                            </DropdownTrigger>
                            <DropdownContent
                                width={270}
                                className="overflow-y-auto"
                            >
                                <>
                                    <DropdownItem
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleColumnFilter(
                                                "coinsurance_outstanding_amount_filter"
                                            );
                                        }}
                                        isSelected={
                                            !coInsuranceOutstandingAmountFilter
                                        }
                                        closeMenuOnSelect
                                    >
                                        All
                                    </DropdownItem>
                                    {coInsuranceAmountOutstandingInClaims &&
                                        coInsuranceAmountOutstandingInClaims.map(
                                            (amount) => (
                                                <DropdownItem
                                                    key={amount}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleColumnFilter(
                                                            "coinsurance_outstanding_amount_filter",
                                                            amount
                                                        );
                                                    }}
                                                    isSelected={
                                                        amount && amount >= 0
                                                            ? coInsuranceOutstandingAmountFilter ===
                                                              String(amount)
                                                            : false
                                                    }
                                                >
                                                    {showMoneyInAppFormat(
                                                        amount
                                                    )}
                                                </DropdownItem>
                                            )
                                        )}
                                </>
                            </DropdownContent>
                        </Dropdown>
                    ),
                },
            ],
        },
        {
            accessorKey: "has_remits",
            header: "HAS REMITS",
            cell: ({ row }) => (
                <span
                    className={
                        row.original.has_remits ? "text-primary" : "text-danger"
                    }
                >
                    {row.original.has_remits ? "Yes" : "No"}
                </span>
            ),
            filterComponent: (
                <Dropdown>
                    <DropdownTrigger asChild>
                        <Button
                            size="auto"
                            variant="can-hover"
                            onClick={(e) => e.stopPropagation()}
                            className="px-1 h-6"
                        >
                            <FilterIcon />
                        </Button>
                    </DropdownTrigger>
                    <DropdownContent width={270}>
                        <>
                            <DropdownItem
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleColumnFilter("has_remits_filter");
                                }}
                                isSelected={!claimsWithRemitsFilter}
                                closeMenuOnSelect
                            >
                                All
                            </DropdownItem>
                            {claimsWithRemits &&
                                claimsWithRemits.map((item) => (
                                    <DropdownItem
                                        key={item}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleColumnFilter(
                                                "has_remits_filter",
                                                item === "Yes"
                                                    ? "true"
                                                    : "false"
                                            );
                                        }}
                                        isSelected={
                                            (item === "Yes" &&
                                                claimsWithRemitsFilter ===
                                                    "true") ||
                                            (item === "No" &&
                                                claimsWithRemitsFilter ===
                                                    "false")
                                        }
                                    >
                                        {item}
                                    </DropdownItem>
                                ))}
                        </>
                    </DropdownContent>
                </Dropdown>
            ),
        },
        {
            accessorKey: "accepts_assignment",
            header: "ACCEPTS ASSIGNMENT",
            cell: ({ row }) => (
                <span
                    className={
                        row.original.accepts_assignment
                            ? "text-primary"
                            : "text-danger"
                    }
                >
                    {row.original.accepts_assignment ? "Yes" : "No"}
                </span>
            ),
            filterComponent: (
                <Dropdown>
                    <DropdownTrigger asChild>
                        <Button
                            size="auto"
                            variant="can-hover"
                            onClick={(e) => e.stopPropagation()}
                            className="px-1 h-6"
                        >
                            <FilterIcon />
                        </Button>
                    </DropdownTrigger>
                    <DropdownContent width={270}>
                        <>
                            <DropdownItem
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleColumnFilter(
                                        "accepts_assignment_filter"
                                    );
                                }}
                                isSelected={!acceptAssignFilter}
                                closeMenuOnSelect
                            >
                                All
                            </DropdownItem>
                            {assignmentInClaims &&
                                assignmentInClaims.map((item) => (
                                    <DropdownItem
                                        key={item}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleColumnFilter(
                                                "accepts_assignment_filter",
                                                item
                                            );
                                        }}
                                        isSelected={acceptAssignFilter === item}
                                    >
                                        {item}
                                    </DropdownItem>
                                ))}
                        </>
                    </DropdownContent>
                </Dropdown>
            ),
        },
        {
            accessorKey: "has_claim_attachments",
            header: "Attached doc",
            cell: ({ row }) => (
                <span
                    className={
                        row.original.has_claim_attachments
                            ? "text-primary"
                            : "text-danger"
                    }
                >
                    {row.original.has_claim_attachments ? "Yes" : "No"}
                </span>
            ),
            filterComponent: (
                <Dropdown>
                    <DropdownTrigger asChild>
                        <Button
                            size="auto"
                            variant="can-hover"
                            onClick={(e) => e.stopPropagation()}
                            className="px-1 h-6"
                        >
                            <FilterIcon />
                        </Button>
                    </DropdownTrigger>
                    <DropdownContent width={270}>
                        <>
                            <DropdownItem
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleColumnFilter("attached_doc_filter");
                                }}
                                isSelected={!attachDocFilter}
                                closeMenuOnSelect
                            >
                                All
                            </DropdownItem>
                            {attachDocInClaims &&
                                attachDocInClaims.map((item) => (
                                    <DropdownItem
                                        key={item}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleColumnFilter(
                                                "attached_doc_filter",
                                                item === "Yes"
                                                    ? "true"
                                                    : "false"
                                            );
                                        }}
                                        isSelected={
                                            (item === "Yes" &&
                                                attachDocFilter === "true") ||
                                            (item === "No" &&
                                                attachDocFilter === "false")
                                        }
                                    >
                                        {item}
                                    </DropdownItem>
                                ))}
                        </>
                    </DropdownContent>
                </Dropdown>
            ),
        },

        {
            accessorKey: "claim_id",
            header: "",
            cell: ({ row }) => {
                const { isClaimAssignedToMe } = getClaimActivityToDisplay(
                    row.original.claim_activities,
                    ClaimActivityType.IN_PROGRESS,
                    loggedInUser?.data?.user_id ?? ""
                );
                return (
                    <Dropdown>
                        <DropdownTrigger asChild>
                            <MoreButton />
                        </DropdownTrigger>
                        <DropdownContent width="auto" align="end">
                            <DropdownItem
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleUpdateClaimsActivity(
                                        isClaimAssignedToMe
                                            ? ClaimActivityType.UNLOCKED
                                            : ClaimActivityType.IN_PROGRESS,
                                        row.original.claim_id
                                    );
                                }}
                                disabled={updateClaimsActivity.isLoading}
                                className="normal-case"
                            >
                                {isClaimAssignedToMe
                                    ? "Unassign"
                                    : "Assign to me"}
                            </DropdownItem>
                            <DropdownItem
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleChangeStatus(
                                        row.original.claim_id,
                                        ClaimStatus.PENDING
                                    );
                                }}
                            >
                                Mark as Pending
                            </DropdownItem>
                            <DropdownItem
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleChangeStatus(
                                        row.original.claim_id,
                                        ClaimStatus.READY_FOR_SUBMISSION
                                    );
                                }}
                            >
                                Mark as Ready for submission
                            </DropdownItem>
                            {row.original.is_voided ? (
                                <DropdownItem
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleChangeVoidStatus(
                                            false,
                                            row.original.claim_id
                                        );
                                    }}
                                >
                                    Unvoid Claim
                                </DropdownItem>
                            ) : (
                                <DropdownItem
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleChangeVoidStatus(
                                            true,
                                            row.original.claim_id
                                        );
                                    }}
                                >
                                    Void Claim
                                </DropdownItem>
                            )}
                        </DropdownContent>
                    </Dropdown>
                );
            },
        },
    ];

    const table = useReactTable({
        columns,
        data: data?.data || [],
        state: {
            columnVisibility,
        },
        getCoreRowModel: getCoreRowModel(),
        onColumnVisibilityChange: setColumnVisibility,
    });

    return (
        <>
            {statusToSubmit && selectedClaimsIds && (
                <Dialog open={dialogInView === "bulk_update_status"}>
                    <DialogContent
                        title="Update Claims status"
                        showFooter
                        saveText="Save changes"
                        cancelText="Cancel"
                        variant="center"
                        handleCloseDialog={handleCloseBulkUpdateStatusDialog}
                        submitBtnFormValue="bulk-update-claims"
                        isSubmitBtnDisabled={isMutating > 0}
                        isCancelBtnDisabled={isMutating > 0}
                    >
                        <BulkUpdateClaimForm
                            claimStatus={statusToSubmit}
                            claimsId={selectedClaimsIds}
                            onFormSubmit={handleCloseBulkUpdateStatusDialog}
                        />
                    </DialogContent>
                </Dialog>
            )}

            <TableFiltersDisplay
                appliedFilters={appliedFilters}
                wrapperClass="mb-[--spacing-items]"
            />

            {error && error?.response?.status !== 404 && (
                <ListState
                    isError
                    stateHelperText="Try reloading this page to solve this issue"
                    errorMsg="Cannot display claims at this time please try again later"
                />
            )}
            {isLoading && <Loader />}
            {data && isSuccess && (
                <>
                    {filteredData.length === 0 && (
                        <ListState
                            stateHelperText="Closed claims will appear here once available "
                            emptyMessage="No claims yet"
                            isError={false}
                        />
                    )}

                    {filteredData.length > 0 && (
                        <>
                            {getTotalAmountOfTableRowsSelected(rowSelection)
                                .amount > 0 && (
                                <div className="flex items-center mb-[--spacing-items] gap-[--spacing-items] flex-wrap">
                                    <div className="font-medium text-base shrink-0">
                                        {
                                            getTotalAmountOfTableRowsSelected(
                                                rowSelection
                                            ).amountString
                                        }
                                        :
                                    </div>

                                    {showAssignClaimsButton ? (
                                        <Button
                                            variant="secondary"
                                            onClick={() =>
                                                handleUpdateClaimsActivity(
                                                    ClaimActivityType.IN_PROGRESS
                                                )
                                            }
                                            disabled={
                                                updateClaimsActivity.isLoading
                                            }
                                        >
                                            Assign claims to me
                                        </Button>
                                    ) : null}
                                    {showUnassignClaimsButton ? (
                                        <Button
                                            variant="secondary"
                                            onClick={() =>
                                                handleUpdateClaimsActivity(
                                                    ClaimActivityType.UNLOCKED
                                                )
                                            }
                                            disabled={
                                                updateClaimsActivity.isLoading
                                            }
                                        >
                                            Unassign claims
                                        </Button>
                                    ) : null}
                                    {allowedClaimStatuses.length > 0 &&
                                    allowedClaimStatuses.length <= 2
                                        ? allowedClaimStatuses.map((status) => (
                                              <Button
                                                  key={status}
                                                  variant="secondary"
                                                  disabled={isMutating > 0}
                                                  onClick={() =>
                                                      handleDisplayBulkUpdateStatusDialog(
                                                          status
                                                      )
                                                  }
                                              >
                                                  Mark as{" "}
                                                  {formatClaimStatus(status)}
                                              </Button>
                                          ))
                                        : null}
                                    {allowedClaimStatuses.length > 2 ? (
                                        <Dropdown>
                                            <DropdownTrigger asChild>
                                                <FilterButton
                                                    disabled={isMutating > 0}
                                                    className="bg-[var(--color-secondary)]"
                                                >
                                                    Update status
                                                </FilterButton>
                                            </DropdownTrigger>
                                            <DropdownContent
                                                width={270}
                                                align="end"
                                            >
                                                {allowedClaimStatuses.map(
                                                    (status) => (
                                                        <DropdownItem
                                                            key={status}
                                                            onClick={() =>
                                                                handleDisplayBulkUpdateStatusDialog(
                                                                    status
                                                                )
                                                            }
                                                        >
                                                            {formatClaimStatus(
                                                                status
                                                            )}
                                                        </DropdownItem>
                                                    )
                                                )}
                                            </DropdownContent>
                                        </Dropdown>
                                    ) : null}
                                    {showUnvoidClaimsButton && (
                                        <Button
                                            variant="secondary"
                                            onClick={() =>
                                                handleChangeVoidStatus(false)
                                            }
                                        >
                                            Unvoid Claims
                                        </Button>
                                    )}

                                    {showVoidClaimsButton && (
                                        <Button
                                            variant="secondary"
                                            onClick={() =>
                                                handleChangeVoidStatus(true)
                                            }
                                        >
                                            Void Claims
                                        </Button>
                                    )}
                                </div>
                            )}
                            <Table
                                columns={columns}
                                data={filteredData}
                                setRowSelection={setRowSelection}
                                rowSelection={rowSelection}
                                setGetSelectedData={setGetSelectedClaimsData}
                                handleRowClick={handleRowClick}
                                hasPagination={data.total_count > 20}
                                isHeaderFixed
                                setSortConfig={setSortConfig}
                                sortConfig={sortConfig}
                                sortableColumns={sortableColumns}
                                showSizeFilter
                                isColumnVisible={columnVisibility}
                                showColumnVisibilityFilter
                                columnVisibilityDropdownItems={
                                    <>
                                        {table
                                            .getAllLeafColumns()
                                            .filter(
                                                (col) => col.id !== "select"
                                            )
                                            .map((column) => {
                                                return (
                                                    <DropdownItem
                                                        key={column.id}
                                                        onClick={() =>
                                                            column.toggleVisibility()
                                                        }
                                                        className="*:!uppercase"
                                                        isMulti
                                                        itemValue={column.id}
                                                        values={table
                                                            .getVisibleLeafColumns()
                                                            .map(
                                                                (col) => col.id
                                                            )}
                                                    >
                                                        {column?.columnDef
                                                            ?.header
                                                            ? `${
                                                                  column?.parent?.id?.trim()
                                                                      ? `${column.parent.id} `
                                                                      : ""
                                                              }${
                                                                  column
                                                                      .columnDef
                                                                      .header
                                                              }`
                                                            : column.id?.replaceAll(
                                                                  "_",
                                                                  " "
                                                              )}
                                                    </DropdownItem>
                                                );
                                            })}
                                    </>
                                }
                                pagination={
                                    <Pagination
                                        totalCount={data.total_count}
                                        totalPages={data.total_pages}
                                        currentPage={pageFilter}
                                        onPageChange={handlePageChange}
                                        limit={limitFilter}
                                        onLimitChange={handleLimitChange}
                                    />
                                }
                            />
                        </>
                    )}
                </>
            )}
        </>
    );
}
